/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Input, Button, message, Spin, Tooltip } from "antd";
import API from "../../../../api";
import urls from "../../../../api/urls";
import Smcs_Loader from "../../../../assests/images/loader-new.gif";
import { InfoCircleOutlined } from "@ant-design/icons";

const { APPLY_DISCOUNT, RETAIL_DISCOUNT_RATE_CARD } = urls;

const DiscountModal = ({
  isModalOpen,
  handleModalClose,
  selectedData,
  name,
  handleInvoiceModalOpen,
  summary,
  setSummary,
}) => {
  useEffect(() => {
    resetSummary();
  }, [selectedData]);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [error, setError] = useState(true);
  const discountDebounceTimeout = useRef(null);

  const handleChange = (e) => {
    const value = e.target.value;

    if (discountDebounceTimeout.current) {
      clearTimeout(discountDebounceTimeout.current);
    }

    discountDebounceTimeout.current = setTimeout(async () => {
      setLoading(true);
      // Reset summary if value is empty or not a number
      if (!value || isNaN(value)) {
        resetSummary();
        setLoading(false);
        return;
      }

      const percentage = parseFloat(value);

      // Validate percentage range
      if (percentage < 1 || percentage > 25) {
        message.warning("Discount percentage must be between 1 and 25%");
        resetSummary();
        setLoading(false);
        return;
      }

      const company =
        JSON.parse(localStorage.getItem("selectedCompany")) || null;
      setDiscountLoading(true);
      setLoading(true);
      const payload = {
        senderName: name,
        discountPercentage: +value,
        documentNos: selectedData?.map((item, index) => item?.documentNo),
        companyId: +company?.key,
      };

      // const response = await API.Api(RETAIL_DISCOUNT_RATE_CARD).post(payload);
      const response = await API.Api(RETAIL_DISCOUNT_RATE_CARD).post(payload);

      if (response?.status) {
        setDiscountLoading(false);
        setLoading(false);
        setError(false);
        const calculatedSummary = response.response.data?.data;

        setSummary({
          discountAmount: calculatedSummary?.discountAmount || 0,
          sgst: calculatedSummary?.rateData?.sgst || 0,
          cgst: calculatedSummary?.rateData?.cgst || 0,
          igst: calculatedSummary?.rateData?.igst || 0,
          cess: calculatedSummary?.rateData?.cess || 0,
          finalAmount: calculatedSummary?.finalAmountWithPackaging || 0,
          packagingCharge: calculatedSummary?.packagingCharge || 0,
          subTotal: calculatedSummary?.subTotal || 0,
          totalCharge: calculatedSummary?.subTotal || 0,
          discountPercentage: calculatedSummary?.discountPercentage || 0,
        });
      } else {
        setLoading(false);
        setDiscountLoading(false);
        setError(true);

        message.error(
          response?.response?.data?.message || "Internal server error"
        );
      }
    }, 600); // 300ms debounce
  };

  const resetSummary = () => {
    setSummary({
      totalAmount: 0,
      discountAmount: 0,
      sgst: 0,
      cgst: 0,
      igst: 0,
      cess: 0,
      finalAmount: 0,
      totalCharge: 0,
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    let payload = selectedData.map((item, index) => {
      return {
        username: item.username,
        documentNo: item.documentNo,
        isBulk: item.isBulk,
        bookingType: item.bookingType,
        areaId: item.areaId,
        shipmentType: item.shipmentType,
        travelBy: item.travelBy,
        deliveryPromise: item.deliveryPromise,
        companyId: item.companyId,
        dwsWeight:
          item?.shipmentType == "DOX"
            ? parseFloat(item.dwsWeight)
            : parseFloat(item.dwsWeight) / 1000,
        dwsLength: parseFloat(item.dwsLength),
        dwsWidth: parseFloat(item.dwsWidth),
        dwsHeight: parseFloat(item.dwsHeight),
        pickupAddress: {
          addressType: item.pickupAddress.addressType,
          name: item.pickupAddress.name,
          email: item.pickupAddress.email,
          gst: item.pickupAddress.gst,
          phone: item.pickupAddress.phone,
          address1: item.pickupAddress.address1,
          address2: item.pickupAddress.address2,
          landmark: item.pickupAddress.landmark,
          city: item.pickupAddress.city,
          state: item.pickupAddress.state,
          country: item.pickupAddress.country,
          zip: item.pickupAddress.zip,
          latitude: item.pickupAddress.latitude,
          longitude: item.pickupAddress.longitude,
        },
        receiverAddress: {
          addressType: item.receiverAddress.addressType,
          name: item.receiverAddress.name,
          email: item.receiverAddress.email,
          gst: item.receiverAddress.gst,
          phone: item.receiverAddress.phone,
          address1: item.receiverAddress.address1,
          address2: item.receiverAddress.address2,
          landmark: item.receiverAddress.landmark,
          city: item.receiverAddress.city,
          state: item.receiverAddress.state,
          country: item.receiverAddress.country,
          zip: item.receiverAddress.zip,
          latitude: item.receiverAddress.latitude,
          longitude: item.receiverAddress.longitude,
        },
        returnAddress: {
          addressType: item.returnAddress.addressType,
          name: item.returnAddress.name,
          email: item.returnAddress.email,
          gst: item.returnAddress.gst,
          phone: item.returnAddress.phone,
          address1: item.returnAddress.address1,
          address2: item.returnAddress.address2,
          landmark: item.returnAddress.landmark,
          city: item.returnAddress.city,
          state: item.returnAddress.state,
          country: item.returnAddress.country,
          zip: item.returnAddress.zip,
          latitude: item.returnAddress.latitude,
          longitude: item.returnAddress.longitude,
        },
        documents:
          item?.documents?.map((doc) => ({
            documentLink: doc?.documentLink,
            documentType: doc?.documentType,
          })) || [],
        eWayBills:
          item?.eWayBills?.map((eway) => ({
            eway: eway?.eway,
            date: eway?.date,
            eway_bill_crt_date: eway?.eway_bill_crt_date,
            ewb_bill_exp_date: eway?.ewb_bill_exp_date,
            trans_mode: eway?.trans_mode,
            name: eway?.name,
          })) || [],
        childShipments:
          item?.childShipments?.map((child) => {
            return {
              documentNo: child?.documentNo,
              dwsHeight: parseFloat(child?.dwsHeight),
              dwsLength: parseFloat(child?.dwsLength),
              dwsWeight: parseFloat(child?.dwsWeight),
              dwsWidth: parseFloat(child?.dwsWidth),
              value: parseFloat(child?.value),
            };
          }) || [],
        charges: {
          amount: parseFloat(item.charges.amount),
          mode: item.charges.mode,
          currency: item.charges.currency,
          totalCharge: parseFloat(item.charges.totalCharge),
          packagingCharge: parseFloat(item.charges.packagingCharge),
        },
        cod: null,
        pod: null,
        metadata: {
          value: parseFloat(item.metadata.value),
          content: item.metadata.content,
          remark: item.metadata.remark,
          insured: item.metadata.insured,
          fov: item.metadata.fov,
          isSez: item.metadata.isSez,
          kycDocumentNo: item.metadata.kycDocumentNo,
          kycDocumentType: item.metadata.kycDocumentType,
        },
        isDiscountBooking: true,
        isDiscountApplied: true,
        // status: item.status,
        discountPercent: +values.discountPercentage,
        status: "BOOKED",
      };
    });
    const user_Info = JSON.parse(localStorage?.getItem("userinfo"));

    const response = await API.Api(APPLY_DISCOUNT).postCustomHeader(payload, {
      cpid: user_Info?.spId,
      user_id: user_Info?.sevasetuUserId,
    });

    if (response?.status) {
      setLoading(false);

      // message.success("Discount applied successfully");
      handleModalClose();
      handleInvoiceModalOpen();
      form.resetFields();
      // resetSummary();
    } else {
      setLoading(false);
      // handleInvoiceModalOpen();
      message.error(
        response?.response?.data?.message || "Failed to apply discount"
      );
    }
  };

  return (
    <Modal
      title="Apply Discount"
      open={isModalOpen}
      onCancel={() => {
        form.resetFields();
        handleModalClose();
      }}
      footer={null}
      width={500}
      className="discount-modal"
    >
      <Spin
        spinning={loading}
        indicator={
          <img
            src={Smcs_Loader}
            style={{ height: "80px", width: "80px", marginLeft: "-40px" }}
          />
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div
            style={{
              backgroundColor: "#FFE8C9",
              padding: "7px",
              borderRadius: 10,
              marginLeft: 0,
              marginBottom: "10px",
            }}
          >
            <span>
              <Tooltip placement="right" title="">
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <span style={{ margin: "0 5px" }}>
              Upto 25% discount can be applied
            </span>
          </div>
          <div>
            <p>
              <b>Sender name:</b>{" "}
              {selectedData && selectedData[0]?.pickupAddress?.name}
            </p>
            <h5>Document Number</h5>

            <p>
              {selectedData?.map((item, index) => (
                <span key={item.documentNo}>
                  {item.documentNo}
                  {index < selectedData.length - 1 ? ", " : ""}
                </span>
              ))}
            </p>
          </div>
          <Form.Item
            name="discountPercentage"
            label="Discount Percentage"
            rules={[
              { required: true, message: "Please enter discount percentage" },
              {
                min: 1,
                max: 25,
                message: "Discount must be between 1 and 25%",
              },
            ]}
          >
            <Input
              type="number"
              onChange={handleChange}
              placeholder="Enter discount percentage"
              maxLength={2}
              // suffix={discountLoading ? <Spin /> : null}
            />
          </Form.Item>

          <div className="discount-summary mt-05">
            <h4 className="summary-heading"> Summary</h4>
            <div className="summary-item">
              <span>Total booking Amount</span>₹
              {`  ${summary?.totalCharge?.toFixed(2)}`}
            </div>
            <div className="summary-item">
              <span>Discount Applied</span>₹
              {` - ${summary?.discountAmount?.toFixed(2) || "0.00"}`}
            </div>
            <div className="summary-item">
              <span>SGST</span>₹{`  ${summary?.sgst?.toFixed(2) || "0.00"}`}
            </div>
            <div className="summary-item">
              <span>CGST</span>₹{`  ${summary?.cgst?.toFixed(2) || "0.00"}`}
            </div>
            <div className="summary-item">
              <span>IGST</span>₹{`  ${summary?.igst?.toFixed(2) || "0.00"}`}
            </div>

            <div className="summary-item">
              <span>CESS</span>₹{`  ${summary?.cess?.toFixed(2) || "0.00"}`}
            </div>
            <div className="summary-item">
              <span>Packaging Charge</span>₹
              {`  ${summary?.packagingCharge?.toFixed(2) || "0.00"}`}
            </div>
            {/* <div className="summary-item">
              <span>Total amount to pay</span>
              <span>₹{summary.totalAmount.toFixed(2)}</span>
            </div> */}
            <div className="summary-divider" />
            <div className="summary-item">
              <span style={{ color: "#3C3C3C" }}>Final amount to pay</span>
              <span style={{ color: "#3C3C3C" }}>
                ₹{`  ${summary?.finalAmount?.toFixed(2) || "0.00"}`}
              </span>
            </div>
          </div>

          <Form.Item>
            <Button
              htmlType="submit"
              block
              className="save-btn"
              // disabled={error || loading}
            >
              SUBMIT
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default DiscountModal;
